/**
 * Expeditions together (decision 0043) in the interface layer: the floating row of members as
 * small avatars (Daniel's choice C of 2026-11-01), standing already on an endless run played alone
 * with Invite beside the player (his choice D of 2026-11-02): an initial, two letters where two
 * share one, a plupp for how each is and their tiles under it; a tap opens a card with the name,
 * how they are, their tiles and distance and Follow. The band says who is followed, each other
 * member's lantern stands over the world where their camera looks, and the sheets invite and join. Plain DOM over the world, in the theme's roles; the main
 * module tells it what to show.
 */
import { THEMES } from "@mistsweeper/assets";
import { encode } from "uqr";
import type { StringKey } from "../i18n.ts";
import { esc, fragment, icon, SQUARE } from "./dom.ts";
import "./party.css";

type T = (key: StringKey, params?: Record<string, string | number>) => string;

/**
 * A link as a QR code in the current colour, in the four modules of quiet zone the format asks for.
 * Medium correction, as the TestFlight code's.
 */
export function qrSvg(link: string, label: string): string {
  const { data, size } = encode(link, { ecc: "M", border: 0 });
  let path = "";
  for (const [y, row] of data.entries()) {
    for (let x = 0; x < size; x++) {
      if (!row[x]) continue;
      const from = x;
      while (row[x + 1]) x++;
      path += `M${from} ${y}H${x + 1}V${y + 1}H${from}z`;
    }
  }
  return `<svg class="qr-code" role="img" aria-label="${esc(label)}" viewBox="-4 -4 ${size + 8} ${size + 8}" shape-rendering="crispEdges"><path fill="currentColor" d="${path}"/></svg>`;
}

export type MemberStatus = "out" | "fell" | "away";

export interface MemberChip {
  readonly m: number;
  readonly name: string;
  readonly you: boolean;
  readonly status: MemberStatus;
  readonly followed: boolean;
  /** The tiles the member's own actions revealed (decision 0043). */
  readonly tiles: number;
  /** The furthest of them, formatted. */
  readonly distance: string;
  /** False where the member gave no name: the avatar shows a person in place of letters. */
  readonly named: boolean;
}

/**
 * The letters on each avatar: the name's first, upper case, or its first two where another named
 * member's name starts with the same letter (Daniel, 2026-11-01). Null for a member with no name.
 */
export function avatarLetters(members: readonly MemberChip[]): Map<number, string | null> {
  const first = (name: string) => Array.from(name.trim())[0]?.toLocaleUpperCase() ?? "";
  const counts = new Map<string, number>();
  for (const m of members)
    if (m.named) counts.set(first(m.name), (counts.get(first(m.name)) ?? 0) + 1);
  const out = new Map<number, string | null>();
  for (const m of members) {
    if (!m.named) {
      out.set(m.m, null);
      continue;
    }
    const letters = Array.from(m.name.trim().replace(/\s+/gu, ""));
    const one = first(m.name);
    out.set(m.m, (counts.get(one) ?? 0) > 1 ? one + (letters[1] ?? "").toLocaleLowerCase() : one);
  }
  return out;
}

/** A person, for a member who gave no name: a head and shoulders, nobody in particular. */
const PERSON = `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7"/></svg>`;

/** An eye, the plupp of a member who fell and watches. */
const EYE = `<svg viewBox="0 0 18 14" width="18" height="14" aria-hidden="true"><path d="M2.5 7C4.5 3.8 13.5 3.8 15.5 7 13.5 10.2 4.5 10.2 2.5 7z" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="9" cy="7" r="1.8" fill="currentColor"/></svg>`;

export interface Lantern {
  readonly m: number;
  readonly name: string;
  /** Where it stands on the screen, in CSS pixels; null when off the screen. */
  readonly at: { readonly x: number; readonly y: number } | null;
  readonly fell: boolean;
}

export interface PartyUiHandlers {
  invite(): void;
  follow(member: number | null): void;
  share(): void;
  leave(): void;
  join(name: string): void;
  dismissJoin(): void;
  /** The host's, from a member's card: take them out of the party. */
  remove(member: number): void;
  /** The host's, from their own card: close the party to new members, or open it. */
  lock(on: boolean): void;
}

/** A small lantern: a ring to hang it by, a roof, the glass with its light, a base. */
const LANTERN = `<svg class="lantern-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><circle cx="12" cy="3.5" r="1.6" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M7 8h10l-2-3H9z" fill="currentColor"/><rect x="8" y="8" width="8" height="10" fill="var(--paper)" stroke="currentColor" stroke-width="1.6"/><circle class="flame" cx="12" cy="13" r="2.2" fill="currentColor"/><path d="M7 18h10v2H7z" fill="currentColor"/></svg>`;

export class PartyUi {
  private readonly t: T;
  private readonly row: HTMLElement;
  private readonly band: HTMLButtonElement;
  private readonly lanterns: HTMLElement;
  private readonly inviteSheet: HTMLElement;
  private readonly joinSheet: HTMLElement;
  private readonly lanternEls = new Map<number, HTMLElement>();
  private readonly card: HTMLElement;
  private rowKey = "";
  private members: readonly MemberChip[] = [];
  /** The member whose card is open, or null. */
  private opened: number | null = null;
  /** Whether this player hosts the party, and whether it is closed to new members. */
  private hosting = false;
  private locked = false;

  constructor(t: T, handlers: PartyUiHandlers) {
    this.t = t;
    this.row = fragment(
      `<div class="party-row" role="group" aria-label="${esc(t("party.members"))}" hidden></div>`,
    );
    this.row.addEventListener("click", (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>("[data-member]");
      if (!target) return;
      const m = target.dataset.member;
      if (m === "more") {
        this.closeCard();
        handlers.invite();
      } else this.toggleCard(Number(m), target);
    });
    this.card = fragment(`
      <div class="party-card" role="dialog" hidden>
        <p class="party-card-name" data-bind="cardName"></p>
        <p class="kicker" data-bind="cardLine"></p>
        <button type="button" class="primary small" data-action="follow"></button>
        <button type="button" class="secondary party-host" data-action="host"></button>
        <button type="button" class="party-leave" data-action="leave">${esc(t("party.leave"))}</button>
      </div>`);
    // Always within reach: the plus and its sheet go once the player has fallen or the party is
    // full, and whoever waits for someone away must not be stuck there.
    this.card.querySelector('[data-action="leave"]')?.addEventListener("click", () => {
      this.closeCard();
      handlers.leave();
    });
    this.card.querySelector('[data-action="host"]')?.addEventListener("click", () => {
      const chip = this.members.find((c) => c.m === this.opened);
      this.closeCard();
      if (!chip || !this.hosting) return;
      if (chip.you) handlers.lock(!this.locked);
      else handlers.remove(chip.m);
    });
    this.card.querySelector('[data-action="follow"]')?.addEventListener("click", () => {
      const m = this.opened;
      const chip = this.members.find((c) => c.m === m);
      this.closeCard();
      if (chip) handlers.follow(chip.followed ? null : chip.m);
    });
    // A tap anywhere else closes the card, as a menu closes.
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (this.opened === null) return;
        const target = event.target as Node;
        if (this.card.contains(target) || this.row.contains(target)) return;
        this.closeCard();
      },
      { capture: true },
    );
    this.band = fragment<HTMLButtonElement>(
      `<button type="button" class="party-band" hidden><span data-bind="following"></span>${icon("close", 16)}</button>`,
    );
    this.band.addEventListener("click", () => handlers.follow(null));
    this.lanterns = fragment('<div class="party-lanterns" aria-hidden="true"></div>');
    // The code is drawn in Day's ink on Day's paper in every theme, as the TestFlight code is.
    this.inviteSheet = fragment(`
      <div class="dialog-layer party-sheet party-invite-sheet" hidden>
        <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="partyInviteTitle">
          <div class="headline">${SQUARE}<h2 id="partyInviteTitle">${esc(t("party.inviteTitle"))}</h2></div>
          <p data-bind="inviteBody"></p>
          <div class="qr party-qr" data-bind="qr" style="color: ${THEMES.day.ink}; background: ${THEMES.day.paper}"></div>
          <p class="party-link" data-bind="link"></p>
          <div class="dialog-buttons">
            <button type="button" class="primary small" data-action="share">${esc(t("party.share"))}</button>
            <button type="button" class="secondary" data-action="close">${esc(t("button.close"))}</button>
          </div>
          <button type="button" class="party-leave" data-action="leave">${esc(t("party.leave"))}</button>
        </div>
      </div>`);
    this.inviteSheet.addEventListener("click", (event) => {
      const action = (event.target as HTMLElement).closest<HTMLElement>("[data-action]")?.dataset
        .action;
      if (action === "share") handlers.share();
      else if (action === "close") this.inviteSheet.hidden = true;
      else if (action === "leave") {
        this.inviteSheet.hidden = true;
        handlers.leave();
      }
    });
    this.joinSheet = fragment(`
      <div class="dialog-layer party-sheet" hidden>
        <form class="dialog" role="dialog" aria-modal="true" aria-labelledby="partyJoinTitle">
          <div class="headline">${SQUARE}<h2 id="partyJoinTitle" data-bind="joinTitle"></h2></div>
          <p data-bind="joinBody"></p>
          <label class="kicker party-label" for="partyName">${esc(t("party.yourName"))}</label>
          <input id="partyName" class="dialog-input" type="text" name="name" maxlength="20" autocomplete="nickname" autocapitalize="words" spellcheck="false" enterkeyhint="go">
          <div class="dialog-buttons">
            <button type="submit" class="primary small">${esc(t("party.join"))}</button>
            <button type="button" class="secondary" data-action="notNow">${esc(t("party.notNow"))}</button>
          </div>
        </form>
      </div>`);
    const form = this.joinSheet.querySelector("form") as HTMLFormElement;
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const input = form.querySelector("input") as HTMLInputElement;
      this.joinSheet.hidden = true;
      handlers.join(input.value);
    });
    this.joinSheet.querySelector('[data-action="notNow"]')?.addEventListener("click", () => {
      this.joinSheet.hidden = true;
      handlers.dismissJoin();
    });
  }

  /** Puts the elements into the interface layer, again whenever the layer was made anew. */
  mount(layer: HTMLElement): void {
    if (this.row.parentElement === layer) return;
    layer.prepend(this.lanterns);
    layer.append(this.row, this.card, this.band, this.inviteSheet, this.joinSheet);
  }

  /**
   * The members' row, or none. On a run played alone (`alone`) it stands with the player alone in
   * it, so a friend steps in beside them; their face then only opens the invitation too.
   */
  setMembers(members: readonly MemberChip[] | null, more: boolean, alone = false): void {
    this.members = alone ? [] : (members ?? []);
    if (this.opened !== null && !this.members.some((m) => m.m === this.opened)) this.closeCard();
    else if (this.opened !== null) this.fillCard();
    const key = JSON.stringify([members, more, alone]);
    if (key === this.rowKey) return;
    this.rowKey = key;
    this.row.hidden = members === null;
    if (!members) {
      this.row.innerHTML = "";
      return;
    }
    const letters = avatarLetters(members);
    const avatars = members.map((m) => {
      const letter = letters.get(m.m);
      const face = letter ? `<span class="party-letters">${esc(letter)}</span>` : PERSON;
      if (alone) {
        return `<span class="party-av" data-member="more" aria-hidden="true"><span class="party-face">${face}</span></span>`;
      }
      const badge =
        m.status === "fell"
          ? `<span class="party-badge is-eye">${EYE}</span>`
          : `<span class="party-badge is-${m.status}"></span>`;
      return `
      <button type="button" class="party-av is-${m.status}${m.you ? " is-you" : ""}${m.followed ? " is-followed" : ""}" data-member="${m.m}" aria-expanded="${this.opened === m.m}" aria-label="${esc(`${this.nameOf(m)}, ${this.lineOf(m)}`)}">
        <span class="party-face">${face}${badge}</span>
        <span class="kicker party-count">${m.tiles}</span>
      </button>`;
    });
    if (more) {
      const invite = esc(this.t("party.invite"));
      // In words until a friend has joined: by then the plus says enough (Daniel, 2026-11-02).
      avatars.push(
        members.length > 1
          ? `<button type="button" class="party-av party-more" data-member="more" aria-label="${invite}"><span class="party-face">+</span></button>`
          : `<button type="button" class="party-av party-more is-worded" data-member="more"><span class="party-face"><span class="party-plus" aria-hidden="true">+</span>${invite}</span></button>`,
      );
    }
    this.row.innerHTML = avatars.join("");
  }

  /** A member's name on their card: the player's own says who it is, or just You with no name. */
  private nameOf(m: MemberChip): string {
    if (!m.you) return m.name;
    return m.named ? this.t("party.youNamed", { name: m.name }) : this.t("party.you");
  }

  private lineOf(m: MemberChip): string {
    const status = this.t(
      m.status === "out" ? "party.out" : m.status === "fell" ? "party.fell" : "party.away",
    );
    return this.t("party.cardLine", {
      status,
      tiles: this.tilesText(m.tiles),
      distance: m.distance,
    });
  }

  private toggleCard(m: number, avatar: HTMLElement): void {
    if (this.opened === m) {
      this.closeCard();
      return;
    }
    this.opened = m;
    this.fillCard();
    this.card.hidden = false;
    // Under the avatar, kept on the screen.
    const at = avatar.getBoundingClientRect();
    const layer = this.card.parentElement?.getBoundingClientRect();
    const width = Math.min(260, (layer?.width ?? 390) - 20);
    const left = Math.max(
      10,
      Math.min(at.left - (layer?.left ?? 0), (layer?.width ?? 390) - width - 10),
    );
    this.card.style.left = `${left}px`;
    this.card.style.width = `${width}px`;
    this.card.style.top = `${at.bottom - (layer?.top ?? 0) + 8}px`;
    for (const el of this.row.querySelectorAll("[data-member]")) {
      el.setAttribute("aria-expanded", String(el.getAttribute("data-member") === String(m)));
    }
  }

  private fillCard(): void {
    const m = this.members.find((c) => c.m === this.opened);
    if (!m) return;
    (this.card.querySelector('[data-bind="cardName"]') as HTMLElement).textContent = this.nameOf(m);
    (this.card.querySelector('[data-bind="cardLine"]') as HTMLElement).textContent = this.lineOf(m);
    const follow = this.card.querySelector<HTMLButtonElement>('[data-action="follow"]');
    if (follow) {
      follow.hidden = m.you || m.status === "away";
      follow.textContent = this.t(m.followed ? "party.unfollow" : "party.follow");
    }
    const host = this.card.querySelector<HTMLButtonElement>('[data-action="host"]');
    if (host) {
      host.hidden = !this.hosting;
      host.textContent = this.t(
        m.you ? (this.locked ? "party.unlock" : "party.lock") : "party.remove",
      );
    }
    (this.card.querySelector('[data-action="leave"]') as HTMLElement).hidden = !m.you;
  }

  /** Whether this player hosts the party, and whether it is closed to new members. */
  setHost(hosting: boolean, locked: boolean): void {
    this.hosting = hosting;
    this.locked = locked;
    if (this.opened !== null) this.fillCard();
  }

  closeCard(): void {
    this.opened = null;
    this.card.hidden = true;
    for (const el of this.row.querySelectorAll("[data-member]"))
      el.setAttribute("aria-expanded", "false");
  }

  /** "84 tiles", in the player's language. */
  tilesText(n: number): string {
    return n === 1 ? this.t("party.tiles1") : this.t("party.tiles", { n });
  }

  /** The band over the world while a member is followed. */
  setFollowing(name: string | null): void {
    this.band.hidden = name === null;
    if (name !== null) {
      (this.band.querySelector('[data-bind="following"]') as HTMLElement).textContent = this.t(
        "party.following",
        { name },
      );
    }
  }

  /** The other members' lanterns, called every frame they may have moved. */
  setLanterns(lanterns: readonly Lantern[]): void {
    const seen = new Set<number>();
    for (const l of lanterns) {
      seen.add(l.m);
      let el = this.lanternEls.get(l.m);
      if (!el) {
        el = fragment(`<div class="lantern">${LANTERN}<span class="lantern-name"></span></div>`);
        this.lanternEls.set(l.m, el);
        this.lanterns.append(el);
      }
      const name = el.querySelector(".lantern-name") as HTMLElement;
      if (name.textContent !== l.name) name.textContent = l.name;
      el.classList.toggle("is-fell", l.fell);
      el.hidden = l.at === null;
      if (l.at) el.style.transform = `translate(${l.at.x.toFixed(1)}px, ${l.at.y.toFixed(1)}px)`;
    }
    for (const [m, el] of this.lanternEls) {
      if (seen.has(m)) continue;
      el.remove();
      this.lanternEls.delete(m);
    }
  }

  showInvite(body: string, link: string, canLeave: boolean): void {
    (this.inviteSheet.querySelector('[data-bind="inviteBody"]') as HTMLElement).textContent = body;
    (this.inviteSheet.querySelector('[data-bind="link"]') as HTMLElement).textContent = link;
    (this.inviteSheet.querySelector('[data-bind="qr"]') as HTMLElement).innerHTML = qrSvg(
      link,
      this.t("party.qrLabel"),
    );
    (this.inviteSheet.querySelector(".party-leave") as HTMLElement).hidden = !canLeave;
    this.inviteSheet.hidden = false;
    this.inviteSheet.querySelector<HTMLElement>('[data-action="share"]')?.focus();
  }

  showJoin(title: string, body: string, name: string): void {
    (this.joinSheet.querySelector('[data-bind="joinTitle"]') as HTMLElement).textContent = title;
    (this.joinSheet.querySelector('[data-bind="joinBody"]') as HTMLElement).textContent = body;
    (this.joinSheet.querySelector("input") as HTMLInputElement).value = name;
    this.joinSheet.hidden = false;
  }

  get sheetOpen(): boolean {
    return !this.inviteSheet.hidden || !this.joinSheet.hidden;
  }
}
